import { ClearAllButton, IconButton } from '@ui/shared/Button';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { IndexRange, ListRowProps } from 'react-virtualized';
import styled, { keyframes } from 'styled-components';
import { LIST_ROW_HEIGHTS } from '../../../constants/list_row_heights';
import { closePopup, openPopup } from '../../../data/providers/popupsProvider';
import { keyMetricsSettingsProvider } from '../../../data/providers/settings/keyMetricsSettingsProvider';
import { exportTests } from '../../../data/providers/testsProvider';
import { formatTimeframe } from '../../../data/providers/utils/formatTimeframe';
import {
  Entity,
  EntityType,
  IKeyMetricsMap,
  IRange,
  ITest,
} from '../../../data/types';
import { IUpdateInPlace } from '../../../hooks/Tests/useInfiniteTests';
import { Column, FullHeightColumn } from '../../layout/Column';
import { Row } from '../../layout/Row';
import { ScrollableArea } from '../../layout/ScrollableArea';
import { InfiniteList } from '../../shared/List/InfiniteList';
import { ListHeader } from '../../shared/List/ListHeader';
import { NoResults } from '../../shared/NoResults';
import { CardFooter } from '../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../shared/card/CardHeader';
import { SelectBox } from '../../shared/form/SelectBox';
import { Toggle } from '../../shared/form/Toggle';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import { Test } from '../test/Test';
import { DateRangePopup } from './DateRangePopup';
import { ExportItem } from './ExportItem';
import { ExportPopup } from './ExportPopup';
import { TestTypePopup } from './TestTypePopup';
import { EditTests } from './edit/EditTests';

interface IProps extends RouteComponentProps {
  data: ITest[];
  loading: boolean;
  onMore: (id: number) => void;
  entity: Entity;
  entityType: EntityType;
  showDisabledTests: boolean;
  dateRange?: IRange;
  facet?: string;
  clearDateRange?: () => void;
  clearFacet?: () => void;
  updateTests: (updateToApply: IUpdateInPlace) => void;
  toggleArchivedTests: () => void;
}

export interface ISelectedTests {
  tests: ITest[];
  ids: Record<string, boolean>;
}

const ExportList = withRouter((props: IProps): React.ReactElement => {
  const listShape = [3, 2, 2, 1];

  const [keyMetrics, setKeyMetrics] = React.useState<IKeyMetricsMap>({});
  const [selectedTests, setSelectedTests] = React.useState<ISelectedTests>({
    ids: {},
    tests: [],
  });
  const [viewedTestId, setViewedTestId] = React.useState<string>();
  const [selectingAll, setSelectingAll] = React.useState(false);

  React.useEffect((): void => {
    clearSelectedTests();
  }, [props.entity?.id, props.entityType]);

  React.useEffect((): (() => void) => {
    const subscriber = keyMetricsSettingsProvider().subscribe(
      (mKeyMetrics: IKeyMetricsMap): void => {
        setKeyMetrics(mKeyMetrics);
      }
    );
    return (): void => subscriber.unsubscribe();
  }, []);

  /**
   * sets a test as selected for editing
   *
   * @param test
   * @param include
   */
  const selectTest = (test: ITest, include: boolean): void => {
    setSelectedTests((mSelectedTests: ISelectedTests): ISelectedTests => {
      const nextSelectedTests = { ...mSelectedTests };
      if (include) {
        nextSelectedTests.tests.push(test);
        nextSelectedTests.ids[test.id] = true;
        return nextSelectedTests;
      } else {
        const ids = {
          ...nextSelectedTests.ids,
        };
        delete ids[test.id];
        return {
          tests: nextSelectedTests.tests.filter(
            (t: ITest): boolean => t.id !== test.id
          ),
          ids,
        };
      }
    });
  };

  /**
   * selects all tests matching the current entity and filters.
   * Uses exportTests to fetch the full unpaginated set, not just loaded rows.
   */
  const selectAllTests = async (): Promise<void> => {
    if (selectingAll) return;
    setSelectingAll(true);
    try {
      const allTests = await exportTests({
        entityId: props.entity?.id,
        entityType: props.entityType,
        range: props.dateRange,
        facet: props.facet,
        showDisabledTests: props.showDisabledTests,
      }).toPromise();
      const ids: Record<string, boolean> = {};
      for (const test of allTests) {
        ids[test.id] = true;
      }
      setSelectedTests({ tests: allTests, ids });
    } finally {
      setSelectingAll(false);
    }
  };

  const selectedCount = Object.keys(selectedTests.ids).length;
  const allSelected =
    props.data?.length > 0 &&
    props.data.every((test: ITest): boolean => Boolean(selectedTests.ids[test.id]));
  const someSelected = selectedCount > 0 && !allSelected;

  /**
   * clears the currently selected tests
   */
  const clearSelectedTests = (): void =>
    setSelectedTests({ tests: [], ids: {} });

  /**
   * navigate to the test view
   *
   * @param uri {string}
   */
  const goToTestView = (test: ITest): void => {
    setViewedTestId(test.id);
    openPopup(
      <Test
        test={test}
        onClose={closePopup(props.history)}
        testList={props.data}
        level={1}
      />,
      props.history,
      `test-${test.id}`
    );
  };

  const rowRenderer = ({
    index,
    key,
    style,
  }: ListRowProps): React.ReactElement => {
    const row = props.data[index];
    return (
      <ExportItem
        keyMetrics={keyMetrics}
        row={row}
        key={key}
        onSelect={selectTest}
        selected={selectedTests.ids[row.id]}
        viewed={row.id === viewedTestId}
        style={style}
        goToTestView={goToTestView}
        shape={listShape}
        showingDisabledTests={props.showDisabledTests}
      />
    );
  };

  const fetchNext = ({ startIndex }: IndexRange): Promise<void> => {
    if (!(props.data?.length > 0)) return;
    const lastTest = props.data[startIndex - 1].timestamp;
    props.onMore(lastTest);
    return Promise.resolve();
  };

  return (
    <FullHeightColumn>
      <CardHeader withListHeader notRounded surface={2}>
        <Column style={{ paddingRight: '10px' }}>
          <Row>
            <CardHeaderLeft style={{ paddingLeft: '10px' }}>
              <DateRangePopup />
              <TestTypePopup />
            </CardHeaderLeft>
            <CardHeaderRight>
              <Row style={{ alignItems: 'center', gap: '8px', justifyContent: 'flex-end' }}>
                {Object.keys(selectedTests.ids).length > 0 && (
                  <ClearAllButton onClick={clearSelectedTests} />
                )}
                <EditTests
                  disabled={Object.keys(selectedTests.ids).length === 0}
                  selectedTests={selectedTests}
                  clearSelectedTests={clearSelectedTests}
                  count={Object.keys(selectedTests.ids).length}
                  updateTests={props.updateTests}
                  showingDisabledTests={props.showDisabledTests}
                />
              </Row>
            </CardHeaderRight>
          </Row>
          <ListHeader
            labels={['Athlete', 'Key Metric', 'Date',
              <SelectBox
                key='select-all'
                ariaLabel='Select all tests'
                disabled={selectingAll}
                onSelect={(_e: React.ChangeEvent<HTMLInputElement>): void => {
                  allSelected || someSelected ? clearSelectedTests() : selectAllTests();
                }}
                selected={allSelected}
                indeterminate={someSelected}
              />
            ]}
            shape={listShape}
          />
        </Column>
      </CardHeader>
      {Boolean(props.dateRange) && (
        <RangeFilterBanner
          range={props.dateRange}
          clear={props.clearDateRange}
        />
      )}
      {Boolean(props.facet) && (
        <FacetFilterBanner facet={props.facet} clear={props.clearFacet} />
      )}
      <ScrollableArea style={{ overflowY: 'hidden' }}>
        {props.data?.length > 0 ? (
          <InfiniteList
            data={props.data}
            rowHeight={LIST_ROW_HEIGHTS.large}
            rowRenderer={rowRenderer}
            onMore={fetchNext}
          />
        ) : (
          !props.loading && <NoResults message='No Tests' />
        )}
      </ScrollableArea>
      {props.loading && <LinearLoading />}
      <CardFooter surface={2} style={{ justifyContent: 'center' }}>
        <div style={{ flex: 1 }}></div>
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ExportPopup
            disabled={props.showDisabledTests}
            entity={props.entity}
            entityType={props.entityType}
            range={props.dateRange}
            facet={props.facet}
          />
        </div>
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <Toggle
            name='Show Disabled Tests'
            label='Show Disabled Tests'
            checked={props.showDisabledTests}
            value='Show'
            onChange={props.toggleArchivedTests}
          />
        </div>
      </CardFooter>
    </FullHeightColumn>
  );
});

export { ExportList };

interface IRangeFilterBannerProps {
  clear: () => void;
  range: IRange;
}
const RangeFilterBanner = (
  props: IRangeFilterBannerProps
): React.ReactElement => {
  const timeframe = formatTimeframe(
    props?.range?.from?.value,
    props?.range?.to?.value,
    true
  );
  return (
    <FilterBannerContainer>
      <BannerMessage>
        <div>Showing tests from: {timeframe}</div>
        <IconButton
          onClick={props.clear}
          icon='clear'
          title='Clear Date Filter'
          color='var(--color-dark-text)'
        />
      </BannerMessage>
    </FilterBannerContainer>
  );
};

interface IFacetFilterBannerProps {
  clear: () => void;
  facet: string;
}
const FacetFilterBanner = (
  props: IFacetFilterBannerProps
): React.ReactElement => {
  return (
    <FilterBannerContainer>
      <BannerMessage>
        <div>Showing only: {props.facet}</div>
        <IconButton
          onClick={props.clear}
          icon='clear'
          title='Clear Test Type Filter'
          color='var(--color-dark-text)'
        />
      </BannerMessage>
    </FilterBannerContainer>
  );
};

const slideDownAnimation = keyframes`
 0% { height: 0px; opacity: 0;}
 100% { height: 30px; opacity: 1; }
`;

const FilterBannerContainer = styled.div`
  height: 30px;
  width: 100%;
  background-color: var(--color-warning-accent);
  color: var(---color-dark-text);
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  animation-name: ${slideDownAnimation};
  animation-duration: 0.3s;
`;

const BannerMessage = styled.div`
  color: var(--color-dark-text);
  flex-direction: row;
  display: flex;
  align-items: center;
  font-weight: 400;

  /* Override IconButton hover to keep transparent background */
  button {
    &:hover {
      background: transparent;
    }
  }
`;
